import dayjs from 'dayjs'

import i18n from '@/libs/i18n'
import type { LocalizedText } from '@/types/Booking'

/**
 * Money, dates and translated text — the three things every screen of this site
 * prints, in one place so they are printed the same way on all of them.
 */

/**
 * The BCP-47 tag Intl is to format with, for each language this site speaks.
 *
 * Not the bare locale code: `de` alone leaves the grouping and the position of the
 * currency symbol to whatever the browser guesses, and a German guest reading a
 * Slovenian-formatted price is being shown somebody else's country's habits.
 */
const INTL_TAGS: Record<string, string> = {
    sl: 'sl-SI', en: 'en-GB', de: 'de-DE', it: 'it-IT', hr: 'hr-HR', hu: 'hu-HU',
}

const intlTag = (): string => INTL_TAGS[i18n.global.locale.value] ?? 'sl-SI'

/**
 * Cents as money.
 *
 * Intl does the formatting, but the DIVISION is the part that matters: the amount
 * arrives as an integer number of cents and stays one until this last step, because a
 * price is never a float anywhere in this product (CLAUDE.md, invariant 4). Dividing
 * here, once, at the edge of the screen, is the only place a fraction is allowed to
 * exist at all.
 */
export const formatMoney = (cents: number | undefined, currency = 'EUR'): string => {
    const amount = (cents ?? 0) / 100

    return new Intl.NumberFormat(intlTag(), {
        style: 'currency', currency, minimumFractionDigits: 2,
    }).format(amount)
}

/**
 * A date as somebody reads it out.
 *
 * Slovenian and English keep the "3. 5. 2026" this site has always printed. The
 * others are written the way a reader in that country writes a date without thinking
 * about it: German runs the dots together, Italian uses slashes, Croatian closes the
 * year with a full stop of its own, and Hungarian counts from the year down.
 */
const DATE_PATTERNS: Record<string, string> = {
    sl: 'D. M. YYYY',
    en: 'D. M. YYYY',
    de: 'D.M.YYYY',
    it: 'D/M/YYYY',
    hr: 'D. M. YYYY.',
    hu: 'YYYY. MM. DD.',
}

export const formatDate = (value: string | undefined): string =>
    value ? dayjs(value).format(DATE_PATTERNS[i18n.global.locale.value] ?? 'D. M. YYYY') : ''

/** A date as the API takes it: 2026-05-03, no time and no offset. */
export const apiDate = (value: string | Date | undefined): string =>
    value ? dayjs(value).format('YYYY-MM-DD') : ''

/**
 * Published text in the reader's own language, or the house's.
 *
 * Never "whichever we found first": a German description shown to a Slovenian reader
 * because it happened to be the only key in the map is worse than nothing, and the
 * house's own language is the one thing everything here is guaranteed to be written
 * in.
 */
export const localized = (text: LocalizedText | undefined, fallbackLanguage: string): string => {
    if (!text) return ''

    return text[i18n.global.locale.value] || text[fallbackLanguage] || ''
}

/**
 * "3 noči" / "1 noč" — the count with the word that agrees with it.
 *
 * The agreement is the message's, not this function's: Slovenian has four forms and a
 * ternary here can only ever pick two. See libs/i18n/index.ts for the rule.
 */
export const nightsLabel = (nights: number): string =>
    i18n.global.t('general.nights', { count: nights }, nights)

/** "2 odrasla, 1 otrok" — children left out when there are none. */
export const guestsLabel = (adults: number, children: number): string => {
    const { t } = i18n.global

    const parts = [t('occupancy.adults', { count: adults }, adults)]

    if (children > 0) {
        parts.push(t('occupancy.children', { count: children }, children))
    }

    return parts.join(', ')
}

/**
 * The board a rate plan includes, in words. An unknown code prints as itself rather
 * than as a blank: a plan carrying a board type this site has not been taught is a
 * thing somebody should see, not a gap in the card.
 */
export const boardLabel = (board: string): string => {
    const known = ['none', 'breakfast', 'half_board', 'full_board', 'all_inclusive']

    return known.includes(board) ? i18n.global.t(`board.${board}`) : board
}
